import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ClipCard, { YoutubeEmbed } from "@/components/ClipCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { User, Mic2, ArrowUp, Swords, Trophy, Skull, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";

export default function ProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = id || user?.id;
  const isOwn = user && userId === user.id;

  const [profile, setProfile] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [votes, setVotes] = useState([]);
  const [battles, setBattles] = useState([]);
  const [tab, setTab] = useState("submissions");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      if (!user) navigate("/login");
      return;
    }
    setLoading(true);
    Promise.all([
      api.get(`/users/${userId}`).then((r) => setProfile(r.data)).catch(() => setProfile(null)),
      api.get(`/users/${userId}/submissions`).then((r) => setSubmissions(r.data)),
      api.get(`/users/${userId}/votes`).then((r) => setVotes(r.data)),
      api.get(`/users/${userId}/battles`).then((r) => setBattles(r.data)),
    ]).finally(() => setLoading(false));
  }, [userId, user, navigate]);

  if (!userId) return null;
  if (loading) return <div className="p-16 text-center text-slate-400">Loading profile...</div>;
  if (!profile) return <div className="p-16 text-center text-slate-400">User not found.</div>;

  const s = profile.stats;
  const joined = new Date(profile.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" });

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 pb-24 md:pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl border border-white/10 p-6 sm:p-8 grain"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,184,0,0.10) 0%, rgba(11,12,16,0.4) 40%, rgba(0,240,255,0.08) 100%)",
        }}
        data-testid="profile-header"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-amber-400 text-black font-display text-4xl glow-amber shrink-0">
            {profile.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-widest text-slate-300">
              <User className="h-3 w-3" />
              {profile.role === "admin" ? "Admin" : "Fan"}{isOwn && " · You"}
            </div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl text-white leading-none" data-testid="profile-name">
              {profile.name}
            </h1>
            <p className="mt-1 flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-widest text-slate-400">
              <CalendarDays className="h-3 w-3" /> Member since {joined}
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StatCard icon={<Mic2 className="h-4 w-4" />} label="Submissions" value={s.submissions} color="amber" testid="stat-submissions" />
          <StatCard icon={<ArrowUp className="h-4 w-4" />} label="Upvotes given" value={s.votes_cast} color="cyan" testid="stat-votes" />
          <StatCard icon={<Swords className="h-4 w-4" />} label="Battles voted" value={s.battles_voted} color="crimson" testid="stat-battles" />
          <StatCard icon={<Trophy className="h-4 w-4" />} label="Upvotes received" value={s.total_upvotes_received} color="amber" testid="stat-received" />
          <StatCard icon={<CalendarDays className="h-4 w-4" />} label="Comments" value={s.comments} color="slate" testid="stat-comments" />
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="mt-8">
        <Tabs value={tab} onValueChange={setTab} data-testid="profile-tabs">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="submissions" data-testid="tab-submissions" className="data-[state=active]:bg-amber-400 data-[state=active]:text-black font-mono-ui uppercase text-xs tracking-widest">
              Submissions ({submissions.length})
            </TabsTrigger>
            <TabsTrigger value="votes" data-testid="tab-votes" className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black font-mono-ui uppercase text-xs tracking-widest">
              Upvoted ({votes.length})
            </TabsTrigger>
            <TabsTrigger value="battles" data-testid="tab-battles" className="data-[state=active]:bg-[#FF2E55] data-[state=active]:text-black font-mono-ui uppercase text-xs tracking-widest">
              Battles ({battles.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-6">
            {submissions.length === 0 ? (
              <EmptyState message={isOwn ? "You haven't submitted a clip yet." : "No submissions yet."} cta={isOwn ? { to: "/submit", label: "Submit your first clip" } : null} />
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {submissions.map((c) => <ClipCard key={c.id} clip={c} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="votes" className="mt-6">
            {votes.length === 0 ? (
              <EmptyState message={isOwn ? "You haven't upvoted anything yet." : "No upvotes cast yet."} cta={isOwn ? { to: "/", label: "Browse leaderboard" } : null} />
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {votes.map((c) => <ClipCard key={c.id} clip={c} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="battles" className="mt-6">
            {battles.length === 0 ? (
              <EmptyState message={isOwn ? "You haven't voted in a battle yet." : "No battle votes yet."} cta={isOwn ? { to: "/battle", label: "Enter the battle" } : null} />
            ) : (
              <div className="space-y-4">
                {battles.map((b) => <BattleRow key={b.id} battle={b} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, testid }) {
  const clsMap = {
    amber: "text-amber-400",
    crimson: "text-[#FF2E55]",
    cyan: "text-[#00F0FF]",
    slate: "text-slate-200",
  };
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={testid}>
      <div className={`flex items-center gap-1.5 ${clsMap[color]}`}>{icon}<span className="text-[9px] font-mono-ui uppercase tracking-widest">{label}</span></div>
      <div className="mt-1 font-display text-3xl text-white">{value}</div>
    </div>
  );
}

function BattleRow({ battle }) {
  if (!battle.winner || !battle.loser) return null;
  return (
    <div className="grid gap-3 rounded-xl border border-white/10 bg-[#12141C] p-4 md:grid-cols-[1fr_auto_1fr]" data-testid={`battle-${battle.id}`}>
      <div className="relative">
        <div className="absolute -top-2 -left-2 z-10 rounded-full bg-amber-400 px-2 py-0.5 text-[9px] font-mono-ui uppercase tracking-widest text-black flex items-center gap-1">
          <Trophy className="h-2.5 w-2.5" />Winner
        </div>
        <Link to={`/clip/${battle.winner.id}`} className="block group">
          <YoutubeEmbed youtubeId={battle.winner.youtube_id} title={battle.winner.title} />
          <p className="mt-2 font-display text-lg text-white group-hover:text-amber-400 line-clamp-2">{battle.winner.title}</p>
          <p className="text-xs text-slate-400"><span className="text-[#00F0FF]">◆</span> {battle.winner.comedian}</p>
        </Link>
      </div>
      <div className="flex items-center justify-center">
        <div className="font-display text-2xl text-[#8B5CF6]">VS</div>
      </div>
      <div className="relative opacity-70">
        <div className="absolute -top-2 -left-2 z-10 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-mono-ui uppercase tracking-widest text-slate-300 flex items-center gap-1">
          <Skull className="h-2.5 w-2.5" />Bombed
        </div>
        <Link to={`/clip/${battle.loser.id}`} className="block group">
          <YoutubeEmbed youtubeId={battle.loser.youtube_id} title={battle.loser.title} />
          <p className="mt-2 font-display text-lg text-white group-hover:text-amber-400 line-clamp-2">{battle.loser.title}</p>
          <p className="text-xs text-slate-400"><span className="text-[#00F0FF]">◆</span> {battle.loser.comedian}</p>
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ message, cta }) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 p-16 text-center">
      <p className="font-display text-2xl text-slate-400">{message}</p>
      {cta && (
        <Link to={cta.to} className="mt-4 inline-block text-amber-400 underline underline-offset-2 hover:text-amber-300">
          {cta.label}
        </Link>
      )}
    </div>
  );
}
